import { Platform } from '@angular/cdk/platform';
import {
    ChangeDetectorRef,
    Component,
    effect,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    viewChild,
    input,
    inject
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { NomenclatureType } from '@app/core/models/nomenclature.model';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ObjetMaquette } from '@app/features/editeur/models/objet-maquette.model';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { ObjetMaquetteService } from '@app/features/editeur/services/objet-maquette.service';
import { TreeViewService } from '@app/features/editeur/services/tree-view.service';
import { IfIsAuthorized } from '@app/shared/autorisations/autorisations.decorator.ts';
import { AutorisationsService } from '@app/shared/autorisations/autorisations.service';
import { Traitement } from '@app/shared/autorisations/traitements';
import { descripteursObjetMaquetteValidators } from '@app/shared/base-form/validators-config';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { Espace, ObjetMaquetteHistory, ObjetMaquetteStructure, TypeEspace, TypeObjetMaquette } from '@pcscol/odf-api-v1-angular';
import { PegaseDialogType } from '@pcscol-portail/composants-pegase';
import { Observable, Subject, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { DescripteursObjetMaquetteComponent } from './descripteurs-objet-maquette/descripteurs-objet-maquette.component';
import { MatCard, MatCardContent } from '@angular/material/card';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMiniFabButton, MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatInput, MatSuffix, MatError } from '@angular/material/input';
import { EnableIfAutoriseDirective } from '../../../../../shared/autorisations/directives/enable-if-autorise.directive';
import { ShowIfAutoriseDirective } from '../../../../../shared/autorisations/directives/show-if-autorise.directive';
import { DescripteursContexteComponent } from './descripteurs-contexte/descripteurs-contexte.component';
import { DescripteursEnqueteComponent } from './descripteurs-enquete/descripteurs-enquete.component';
import { DescripteursFormatEnseignementsComponent } from './descripteurs-format-enseignements/descripteurs-format-enseignements.component';
import { DescripteursSyllabusComponent } from './descripteurs-syllabus/descripteurs-syllabus.component';
import { PerimetresComponent } from './perimetres/perimetres.component';
import { MatDivider } from '@angular/material/list';

interface Perimetre {
    code: string;
    libelle: string;
}

@Component({
    selector: 'app-editeur-objet-maquette',
    templateUrl: './editeur-objet-maquette.component.html',
    styleUrls: ['./editeur-objet-maquette.component.scss'],
    providers: [{ provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { disableToggleValue: true } }],
    imports: [
        MatCard,
        MatCardContent,
        MatTabGroup,
        NgClass,
        MatTab,
        MatTabLabel,
        MatIcon,
        MatTooltip,
        MatMiniFabButton,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatSuffix,
        EnableIfAutoriseDirective,
        MatError,
        MatSlideToggle,
        DescripteursObjetMaquetteComponent,
        MatButton,
        ShowIfAutoriseDirective,
        DescripteursContexteComponent,
        DescripteursEnqueteComponent,
        DescripteursFormatEnseignementsComponent,
        DescripteursSyllabusComponent,
        PerimetresComponent,
        MatDivider,
        AsyncPipe
    ]
})
export class EditeurObjetMaquetteComponent implements OnChanges, OnDestroy, OnInit {
    private objetMaquetteService = inject(ObjetMaquetteService);
    private commandFactoryService = inject(CommandFactoryService);
    private fb = inject(NonNullableFormBuilder);
    private treeview = inject(TreeViewService);
    private notificationService = inject(NotificationService);
    private detector = inject(ChangeDetectorRef);
    private dialogService = inject(DialogService);
    private _autorisationsService = inject(AutorisationsService);
    private _platform = inject(Platform);

    readonly ODF_MODIFIER_CODE_OBJET_MAQUETTE = Traitement.ODF_MODIFIER_CODE_OBJET_MAQUETTE;
    readonly ODF_RENDRE_MUTUALISE = Traitement.ODF_RENDRE_MUTUALISE;
    readonly ODF_SUPPRIMER_MUTUALISE = Traitement.ODF_SUPPRIMER_MUTUALISE;
    readonly ODF_LISTER_CONTEXTES = Traitement.ODF_LISTER_CONTEXTES;
    readonly ODF_LIRE_HISTORIQUE = Traitement.ODF_LIRE_HISTORIQUE;
    readonly ODF_ASSOCIER_OBJET_MAQUETTE_PERIMETRE = Traitement.ODF_ASSOCIER_OBJET_MAQUETTE_PERIMETRE;

    readonly NomenclatureType = NomenclatureType;
    readonly TypesObjetsMaquette = TypeObjetMaquette;
    @ViewChild('modifierCodeConfirmationDialogTemplate') modifierCodeConfirmationDialogTemplate: TemplateRef<unknown>;
    @ViewChild('descripteursObjetMaquetteComponent') descripteursObjetMaquetteComponent: DescripteursObjetMaquetteComponent;
    matTabGroup = viewChild<MatTabGroup>(MatTabGroup);
    modifierCodeConfirmationDialog: MatDialogRef<unknown>;
    private destroy$ = new Subject<void>();
    @Input() omStructure: ObjetMaquetteStructure;
    readonly contexteId = input<string | null>();
    @Input() espace: Espace;
    @Output() closeSelectedObjetMaquette = new EventEmitter<void>();
    objetMaquetteSelected?: ObjetMaquette;
    historique?: ObjetMaquetteHistory;
    codeForm: FormGroup;
    tabsLayout$ = this.treeview.tabsLayout$;
    private loadObjetMaquetteSubscription = new Subscription();
    private _selectedIndex: number = 1;
    private disabledTabs = {
        [TypeObjetMaquette.Groupement]: [3, 4, 5],
        [TypeObjetMaquette.Formation]: [2],
        [TypeObjetMaquette.ObjetFormation]: []
    } as Record<TypeObjetMaquette, number[]>;

    private observer?: IntersectionObserver;

    get isBacASable(): boolean {
        return this.espace.typeEspace === TypeEspace.BacASable;
    }

    get selectedIndex(): number {
        if (this.objetMaquetteSelected && this.disabledTabs[this.objetMaquetteSelected.typeObjetMaquette!].includes(this._selectedIndex)) {
            return 1;
        } else if (this.isHistoriqueDisabled && this._selectedIndex === 6) {
            return 1;
        }
        return this._selectedIndex;
    }

    set selectedIndex(i: number) {
        this._selectedIndex = i;
    }

    get isHistoriqueDisabled(): boolean {
        return !this.historique || this.historique.items?.length === 0;
    }

    someTabLabelIsOverflowing: boolean = false;

    constructor() {
        this.codeForm = this.fb.group({
            code: this.fb.control({ value: null, disabled: true }, { validators: descripteursObjetMaquetteValidators.code })
        });
        this.ifFirefoxObserveIntersectionIfTabsHeader();
    }

    mesPerimetres$: Observable<Perimetre[]> = this._autorisationsService.mesPerimetres$();

    private ifFirefoxObserveIntersectionIfTabsHeader() {
        if (this._platform.FIREFOX) {
            this.observeTabHeader();
            effect(() => {
                if (this.matTabGroup()) {
                    const tabHeader = this.matTabGroup()?._tabHeader?._tabListInner?.nativeElement;
                    if (tabHeader) {
                        this.observer?.observe(tabHeader);
                    }
                }
            });
        }
    }

    /**
     * IntersectionObserver pour aouter du padding à droite du tab-header en cas d'overflow, pour éviter que la scrollbar ne cache les labels (sur firefox)
     */
    private observeTabHeader() {
        this.observer = new IntersectionObserver(tabHeaderContainer => {
            if (tabHeaderContainer[0].intersectionRatio > 0 && tabHeaderContainer[0].intersectionRatio < 1) {
                this.someTabLabelIsOverflowing = true;
            } else {
                this.someTabLabelIsOverflowing = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
        this.loadObjetMaquetteSubscription.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.treeview.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadObjetMaquette();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.omStructure) {
            setTimeout(() => {
                this.loadObjetMaquette();
            });
        }
    }

    loadObjetMaquette() {
        this.objetMaquetteSelected = undefined;
        this.loadObjetMaquetteSubscription = this.objetMaquetteService
            .lireObjetMaquette(this.omStructure.id)
            .pipe(first(), takeUntil(this.destroy$))
            .subscribe((objetMaquetteRecu: ObjetMaquette) => {
                this.objetMaquetteSelected = objetMaquetteRecu;
                this.codeForm.patchValue({ code: this.objetMaquetteSelected.code });
                this.codeForm.disable();
                this.detector.detectChanges();
            });

        if (this.espace.typeEspace == TypeEspace.Periode) {
            // DISABLE HISTORIQUE
            // this.chargerHistorique();
        } else {
            // pour le bac à sable, pas d'historique
            this.historique = undefined;
        }
    }

    @IfIsAuthorized(Traitement.ODF_LIRE_HISTORIQUE)
    private chargerHistorique() {
        this.objetMaquetteService
            .lireHistorique(this.omStructure.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((historique: ObjetMaquetteHistory) => {
                this.historique = historique;
            });
    }

    fermerFormulaire() {
        this.closeSelectedObjetMaquette.emit();
    }

    toggleMutualiseHandler(): void {
        if (!this.descripteursObjetMaquetteComponent.form.isPristine) {
            this.dialogService
                .openConfirmationDialog({
                    titre: "Les descripteurs de l'objet sont en cours de modification",
                    messageTexte: [
                        `Les descripteurs de l'objet comportent des modifications non sauvegardées.`,
                        `Si vous confirmez le changement du caractère mutualisé, ces modifications seront perdues.`,
                        `Veuillez confirmer.`
                    ]
                })
                .subscribe((confirmation: boolean | undefined) => {
                    if (confirmation) {
                        this.toggleMutualise();
                    }
                });
        } else {
            this.toggleMutualise();
        }
    }

    openModifierCodeConfirmationDialog(): void {
        this.modifierCodeConfirmationDialog = this.dialogService.openDialog({
            titre: 'Confirmez la modification du code',
            type: PegaseDialogType.WARN,
            template: this.modifierCodeConfirmationDialogTemplate
        });
    }

    modifierCode(): void {
        this.codeForm.enable();
    }

    enregistrerCode(): void {
        if (this.objetMaquetteSelected?.id && this.objetMaquetteSelected?.version && this.objetMaquetteSelected?.code) {
            this.commandFactoryService
                .modifierCodeObjetMaquetteCommand(
                    this.objetMaquetteSelected.id!,
                    this.objetMaquetteSelected.version!,
                    this.objetMaquetteSelected.code!,
                    this.codeForm.value.code
                )
                .do()
                .pipe(first())
                .subscribe(om => {
                    this.objetMaquetteSelected = om;
                    this.treeview.refresh();
                    this.notificationService.success(`Le code de l'objet maquette a été modifié avec succès`);
                });
        }
    }

    annulerModificationCode(): void {
        this.codeForm.setValue({
            code: this.objetMaquetteSelected?.code
        });
        this.codeForm.markAsPristine();
        this.codeForm.disable();
    }

    canSaveCode(): boolean {
        return this.objetMaquetteSelected?.code !== this.codeForm.value.code && this.codeForm.dirty && this.codeForm.valid;
    }

    private toggleMutualise(): void {
        if (this.objetMaquetteSelected?.id && this.objetMaquetteSelected?.version) {
            const command = this.objetMaquetteSelected?.mutualise
                ? this.commandFactoryService.supprimerMutualiseCommand(this.objetMaquetteSelected.id!, this.objetMaquetteSelected.version!)
                : this.commandFactoryService.rendreMutualiseCommand(this.objetMaquetteSelected.id!, this.objetMaquetteSelected.version!);
            const successMessage = this.objetMaquetteSelected?.mutualise
                ? `Le caractère mutualisé de l'objet maquette a été modifié avec succès pour cet objet`
                : `Le caractère mutualisé de l'objet maquette a été modifié avec succès pour cet objet et pour toute sa descendance`;
            command
                .do()
                .pipe(first())
                .subscribe((om: ObjetMaquette) => {
                    this.objetMaquetteSelected = om;
                    this.treeview.refresh();
                    this.notificationService.success(successMessage);
                });
        }
    }

    toggleTabsLayout() {
        this.treeview.toggleTabsLayout();
    }
}
